/**
 * useDosernePush — enables/disables Web Push for Daily-Plan broadcasts.
 *
 * Flow:
 *  1. Frontend fetches the VAPID public key from `/api/doserne/push/public-key`.
 *  2. User clicks "Enable notifications" → we request Notification permission.
 *  3. Subscribe the service-worker registration with the VAPID key.
 *  4. POST the subscription to `/api/doserne/push/subscribe` (upsert).
 *  5. On disable: PushManager.unsubscribe() + DELETE via /push/unsubscribe.
 *
 * Persists state under localStorage.DOSERNE_PUSH_ENABLED for quick UI.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function b64urlToUint8Array(b64url) {
  const pad = "=".repeat((4 - (b64url.length % 4)) % 4);
  const b64 = (b64url + pad).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function useDosernePush(authHeader) {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (typeof window === "undefined") return;
    const ok = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setSupported(ok);
    if (!ok) return;
    setPermission(Notification.permission);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) { setSubscribed(false); return; }
      let sub = await reg.pushManager.getSubscription();

      // Auto-heal: permission was granted before but the subscription
      // itself is gone (browser reset, VAPID rotated, prod redeploy…).
      // Silently re-create it so the "Test-varsel" button actually
      // has a row in the backend to send to. This is the root cause
      // of the recurring "notifications don't work even though they
      // look active" bug — permission stays granted but the sub
      // vanishes and the app never notices.
      if (!sub && Notification.permission === "granted" && typeof authHeader === "function") {
        try {
          const { data } = await axios.get(`${API}/doserne/push/public-key`, authHeader());
          const pubKey = data.public_key;
          if (pubKey) {
            sub = await reg.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: b64urlToUint8Array(pubKey),
            });
          }
        } catch (_e) { /* silent — user can still hit Enable manually */ }
      }

      setSubscribed(!!sub);
      // Silent re-sync: if the user is already subscribed AND has already
      // granted permission, re-POST the subscription to the backend on
      // every Doserne app mount. This heals two common breakage cases:
      //   • The browser rotated the push endpoint (Chrome sometimes does)
      //   • The backend was redeployed and lost the row (env swap etc.)
      //   • VAPID keys were rotated — the new sub replaces the old one
      // Only runs when we know auth is available (authHeader is a fn).
      if (sub && Notification.permission === "granted" && typeof authHeader === "function") {
        try {
          const j = sub.toJSON();
          await axios.post(
            `${API}/doserne/push/subscribe`,
            {
              endpoint: j.endpoint,
              keys: j.keys,
              user_agent: navigator.userAgent,
              is_standalone:
                window.matchMedia("(display-mode: standalone)").matches ||
                window.navigator.standalone === true,
            },
            authHeader(),
          );
        } catch { /* silent — this is a background heal */ }
      }
    } catch { setSubscribed(false); }
  }, [authHeader]);

  useEffect(() => { refresh(); }, [refresh]);

  const enable = useCallback(async () => {
    if (!supported) throw new Error("Push not supported on this browser");
    setBusy(true);
    try {
      // 1. Ask permission.
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") throw new Error("Permission denied");

      // 2. Grab the public VAPID key from backend.
      const { data } = await axios.get(`${API}/doserne/push/public-key`, authHeader());
      const pubKey = data.public_key;
      if (!pubKey) throw new Error("Server missing VAPID key");

      // 3. Wait for the SW to be ready and subscribe.
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: b64urlToUint8Array(pubKey),
        });
      }

      // 4. Persist on backend. Include user_agent + is_standalone so
      //    the server can dedupe (Safari tab vs Home-Screen PWA on
      //    the same iPhone were creating two subs = two pushes).
      const j = sub.toJSON();
      await axios.post(
        `${API}/doserne/push/subscribe`,
        {
          endpoint: j.endpoint,
          keys: j.keys,
          user_agent: navigator.userAgent,
          is_standalone:
            window.matchMedia("(display-mode: standalone)").matches ||
            window.navigator.standalone === true,
        },
        authHeader(),
      );
      setSubscribed(true);
      localStorage.setItem("DOSERNE_PUSH_ENABLED", "1");
    } finally { setBusy(false); }
  }, [supported, authHeader]);

  const disable = useCallback(async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub) {
        try {
          await axios.post(
            `${API}/doserne/push/unsubscribe`,
            { endpoint: sub.endpoint, keys: sub.toJSON().keys || {} },
            authHeader(),
          );
        } catch { /* best effort */ }
        await sub.unsubscribe();
      }
      setSubscribed(false);
      localStorage.removeItem("DOSERNE_PUSH_ENABLED");
    } finally { setBusy(false); }
  }, [authHeader]);

  return { supported, permission, subscribed, busy, enable, disable, refresh };
}
